import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, Copy, CreditCard, ExternalLink, FileText, Landmark, Loader2, RefreshCw, Send } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { mensajeDeError } from '@/lib/errores'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import LinkDePago from '@/pages/Clientes/ficha/LinkDePago'
import { dinero, fecha } from '@/pages/Clientes/ficha/formato'

// Mientras se espera la transferencia no hay pasarela a la que preguntar: lo que cambia es que el
// cliente suba un comprobante o que un administrador registre el pago. Se relee la propuesta.
const TRANSFERENCIA_CADA_MS = 30000
const TRANSFERENCIA_HASTA_MS = 60 * 60 * 1000

const ESTADO_COMPROBANTE = {
  pendiente: { label: 'En revisión', variant: 'warning' },
  aceptado: { label: 'Aceptado', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'danger' },
}

/**
 * COBRO de una cotización ACEPTADA (docs/180).
 *
 * Antes el asistente no tenía ninguna acción de cobro: el enlace solo salía de un icono sin texto
 * en la lista, se veía en un toast de 10 s, y para saber si el cliente pagó había que recargar.
 * Ahora, al aceptar, el dueño va directo al pago; aquí el asesor ve ese mismo enlace (o lo genera
 * si el dueño no llegó a pagar), lo copia o lo manda por WhatsApp, y el estado se actualiza solo.
 *
 * Wompi no acepta enlaces de más de USD 1,000: por encima, la API manda el cobro por
 * TRANSFERENCIA BANCARIA. Entonces el `url` ya no es una pasarela sino la cotización pública del
 * cliente (cuentas, referencia y dónde subir el comprobante), y el pago lo registra un
 * administrador. Esta tarjeta lo distingue por el proveedor de la respuesta o del `pago` guardado.
 */
export default function TarjetaCobro({ c }) {
  const id = c.propuesta?._id
  const [enlace, setEnlace] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [generando, setGenerando] = useState(false)
  // Error del último intento de generar, a la vista además del toast (p. ej. 409
  // MONTO_SUPERA_TOPE: el total pasa del tope y no hay cuenta bancaria configurada).
  const [error, setError] = useState('')
  // `c` es un objeto nuevo en cada render: se guardan sus funciones en refs para no re-disparar efectos.
  const recargar = useRef(c.cargar)
  recargar.current = c.cargar
  const releer = useRef(c.recargarPropuesta)
  releer.current = c.recargarPropuesta
  const alPagar = useCallback(() => recargar.current?.(), [])

  const leer = useCallback(async () => {
    try {
      const r = (await cd2bPropuestaService.estadoPago(id))?.data || {}
      if (r.pagada) { alPagar(); return }
      setEnlace(r.enlace || null)
    } catch { /* sin estado: se ofrece generar */ } finally { setCargando(false) }
  }, [id, alPagar])

  useEffect(() => { if (id) leer() }, [id, leer])

  const verificar = useCallback(async () => (await cd2bPropuestaService.estadoPago(id))?.data || {}, [id])

  // ¿El cobro va por transferencia? Lo dice la última respuesta de «generar» o el `pago` ya
  // guardado en la propuesta (al volver a abrir la cotización). `estado-pago` no distingue el
  // proveedor de un `pago.url`, así que lo guardado manda sobre su `enlace`.
  const pagoGuardado = c.propuesta?.pago
  const transferenciaGuardada = pagoGuardado?.proveedor === 'transferencia' && !pagoGuardado?.anuladoAt
    ? pagoGuardado
    : null
  const respuestaTransferencia = enlace?.proveedor === 'transferencia' ? enlace : null
  const transferencia = respuestaTransferencia || transferenciaGuardada
    ? { ...(transferenciaGuardada || {}), ...(respuestaTransferencia || {}) }
    : null

  const generar = async () => {
    setGenerando(true)
    setError('')
    try {
      const d = (await cd2bPropuestaService.generarLink(id))?.data || {}
      if (d.url) {
        if (d.proveedor === 'transferencia') {
          setEnlace({
            url: d.url, proveedor: 'transferencia', neto: d.neto, iva: d.iva, total: d.total,
            referencia: d.referencia, cuentas: Array.isArray(d.cuentas) ? d.cuentas : [],
          })
          toast.success(d.correoEnviado === false
            ? 'Cobro por transferencia listo. No se pudo enviar el correo: copia el enlace y mándaselo al cliente.'
            : 'Datos de la transferencia enviados al correo del cliente.', { duration: 8000 })
          // Que `pago.proveedor` y los comprobantes queden en la propuesta sin desmontar el editor.
          releer.current?.()
        } else {
          setEnlace({ url: d.url, proveedor: d.proveedor, neto: d.neto, iva: d.iva, total: d.total, esPrueba: !!d.esPrueba })
          toast.success(d.correoEnviado === false
            ? 'Link listo. No se pudo enviar el correo: cópialo abajo y mándaselo.'
            : 'Link listo y enviado al correo del cliente.')
        }
      }
    } catch (e) {
      const msg = mensajeDeError(e, 'No se pudo generar el link de pago')
      setError(msg)
      toast.error(msg, { duration: 10000 })
    } finally { setGenerando(false) }
  }

  return (
    <Card id="cobro" className="scroll-mt-24">
      <CardHeader><CardTitle className="text-h3 flex items-center gap-2"><CreditCard size={18} /> Cobro</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {transferencia ? (
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            El total supera el máximo de USD 1,000 que Wompi acepta por enlace, así que este cobro va por
            transferencia bancaria. El cliente recibió por correo las cuentas y la referencia; cuando
            transfiera, sube el comprobante en su cotización y un administrador registra el pago.
          </p>
        ) : (
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            El cliente aceptó la cotización. Al aceptar lo llevamos directo al pago; si no lo completó,
            compártele el link. Cuando pague, la cotización pasa sola a «Pagada».
          </p>
        )}
        {cargando ? (
          <p className="text-body-sm text-neutral-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Revisando el cobro…</p>
        ) : transferencia ? (
          <PanelTransferencia
            datos={transferencia}
            comprobantes={c.propuesta?.transferencia?.comprobantes || []}
            moneda={c.form?.moneda || 'USD'}
            reenviar={generar}
            reenviando={generando}
            releer={releer}
            onPagada={alPagar}
          />
        ) : enlace ? (
          <LinkDePago
            link={enlace}
            moneda={c.form?.moneda || 'USD'}
            verificar={verificar}
            onPagada={alPagar}
            textoPagado="¡Pago recibido! Ya puedes entregar la empresa a producción."
            textoMensaje="Hola, este es el link para pagar tu suscripción cd-2b"
          />
        ) : (
          <Button onClick={generar} disabled={generando}>
            <CreditCard size={15} className="mr-1.5" /> {generando ? 'Generando…' : 'Generar link de pago'}
          </Button>
        )}
        {error && (
          <p role="alert" className="text-body-sm text-danger-700 dark:text-danger-300 flex items-start gap-1.5">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {error}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Cobro por transferencia: total, referencia, cuentas y comprobantes del cliente.
 *
 * El asesor no registra el pago (lo hace un administrador al revisar el comprobante): aquí solo
 * ve en qué va y tiene a mano el enlace y el reenvío. Se relee la propuesta cada 30 s mientras la
 * pantalla está visible; cuando pasa a «Pagada» se recarga el editor entero.
 */
function PanelTransferencia({ datos, comprobantes, moneda, reenviar, reenviando, releer, onPagada }) {
  const [copiado, setCopiado] = useState('')
  const [revisando, setRevisando] = useState(false)
  const [agotado, setAgotado] = useState(false)
  const cuentas = Array.isArray(datos.cuentas) ? datos.cuentas : []
  const enRevision = comprobantes.some((x) => x.estado === 'pendiente')

  const revisar = useCallback(async ({ manual = false } = {}) => {
    setRevisando(true)
    try {
      const p = await releer.current?.()
      if (!p) {
        if (manual) toast.error('No se pudo consultar el cobro ahora.')
        return
      }
      if (['pagada', 'activada'].includes(p.estado)) {
        toast.success('¡Pago registrado! Ya puedes entregar la empresa a producción.', { duration: 8000 })
        onPagada?.()
      } else if (manual) {
        toast('Todavía no hay pago registrado.', { icon: '⏳' })
      }
    } finally {
      setRevisando(false)
    }
  }, [releer, onPagada])

  useEffect(() => {
    const desde = Date.now()
    const t = setInterval(() => {
      if (Date.now() - desde > TRANSFERENCIA_HASTA_MS) {
        clearInterval(t)
        setAgotado(true)
        return
      }
      if (document.visibilityState === 'visible') revisar()
    }, TRANSFERENCIA_CADA_MS)
    return () => clearInterval(t)
  }, [revisar])

  const copiar = async (texto, clave) => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(clave)
      setTimeout(() => setCopiado((k) => (k === clave ? '' : k)), 2500)
    } catch {
      toast.error('No se pudo copiar: selecciónalo y cópialo a mano.')
    }
  }

  return (
    <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-900/20 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-1.5">
          <Landmark size={15} /> Cobro por transferencia bancaria
        </p>
        <Badge variant={enRevision ? 'secondary' : 'warning'}>
          {enRevision ? 'Comprobante en revisión' : 'Esperando la transferencia'}
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-caption text-neutral-500 dark:text-neutral-400">Total a transferir</p>
          <p className="text-h3 font-semibold text-neutral-900 dark:text-neutral-50">
            {datos.total != null ? dinero(datos.total, moneda) : '—'}
          </p>
          {datos.neto != null && (
            <p className="text-caption text-neutral-600 dark:text-neutral-400">
              neto {dinero(datos.neto, moneda)} + IVA {dinero(datos.iva, moneda)}
            </p>
          )}
        </div>
        {datos.referencia && (
          <div>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">Referencia</p>
            <div className="flex items-center gap-2">
              <span className="font-mono text-body-sm font-semibold text-neutral-900 dark:text-neutral-50 break-all">{datos.referencia}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copiar(datos.referencia, 'referencia')}
                aria-label="Copiar la referencia"
              >
                {copiado === 'referencia' ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
            <p className="text-micro text-neutral-500 dark:text-neutral-400">El cliente la pone en el concepto de la transferencia.</p>
          </div>
        )}
      </div>

      {cuentas.length > 0 && (
        <div className="space-y-2">
          <p className="text-caption font-semibold text-neutral-700 dark:text-neutral-300">
            {cuentas.length === 1 ? 'Cuenta para transferir' : 'Cuentas para transferir'}
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {cuentas.map((cu, i) => (
              <li key={`${cu.banco}-${cu.numero}-${i}`} className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 text-caption space-y-0.5">
                <p className="font-semibold text-neutral-900 dark:text-neutral-50">{cu.banco || 'Banco'}</p>
                <p className="text-neutral-600 dark:text-neutral-400">
                  {[cu.tipo, cu.moneda].filter(Boolean).join(' · ')}
                </p>
                {cu.numero && (
                  <p className="font-mono text-neutral-900 dark:text-neutral-50 break-all">{cu.numero}</p>
                )}
                {cu.titular && <p className="text-neutral-600 dark:text-neutral-400">A nombre de {cu.titular}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => copiar(datos.url, 'enlace')} disabled={!datos.url} variant={copiado === 'enlace' ? 'outline' : 'default'}>
          {copiado === 'enlace'
            ? <><Check size={15} className="mr-1.5" /> Copiado</>
            : <><Copy size={15} className="mr-1.5" /> Copiar enlace para el cliente</>}
        </Button>
        {datos.url && (
          <Button variant="outline" asChild>
            <a href={datos.url} target="_blank" rel="noopener noreferrer" aria-label="Abrir la cotización del cliente">
              <ExternalLink size={15} />
            </a>
          </Button>
        )}
        <Button variant="outline" onClick={reenviar} disabled={reenviando}>
          <Send size={15} className="mr-1.5" /> {reenviando ? 'Reenviando…' : 'Reenviar datos al cliente'}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-caption font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5">
          <FileText size={14} /> Comprobantes del cliente
        </p>
        {comprobantes.length === 0 ? (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">El cliente aún no ha subido el comprobante.</p>
        ) : (
          <ul className="space-y-2">
            {comprobantes.map((cp, i) => {
              const est = ESTADO_COMPROBANTE[cp.estado] || ESTADO_COMPROBANTE.pendiente
              return (
                <li key={cp._id || i} className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 text-caption">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-neutral-900 dark:text-neutral-50 break-all">{cp.nombre || 'Comprobante'}</span>
                    <Badge variant={est.variant}>{est.label}</Badge>
                  </div>
                  <p className="text-neutral-600 dark:text-neutral-400 mt-0.5">
                    {[
                      cp.monto != null ? dinero(cp.monto, moneda) : null,
                      cp.fecha ? `transferido el ${fecha(cp.fecha)}` : null,
                      cp.subidoAt ? `subido el ${fecha(cp.subidoAt)}` : null,
                    ].filter(Boolean).join(' · ')}
                  </p>
                  {cp.estado === 'rechazado' && cp.motivoRechazo && (
                    <p className="text-danger-700 dark:text-danger-300 mt-1">Motivo: {cp.motivoRechazo}</p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => revisar({ manual: true })} disabled={revisando}>
          <RefreshCw size={14} className={`mr-1.5 ${revisando ? 'animate-spin' : ''}`} /> Revisar ahora
        </Button>
        <span className="text-micro text-neutral-500 dark:text-neutral-400">
          {agotado
            ? 'Dejamos de revisar solos. Pulsa «Revisar ahora» cuando el cliente te diga que transfirió.'
            : 'Se revisa solo cada 30 segundos mientras tengas esta pantalla abierta.'}
        </span>
      </div>
    </div>
  )
}
